<template>
  <div class="inventory-item-sidesheet" @keydown.esc="close">
    <remove-item-modal ref="removeItemModal" @remove="onRemove"></remove-item-modal>

    <c-sidesheet :active="active" @close="close">
      <div class="modal-frame">
        <div class="modal-wrapper">
          <shadow-scroll-container ref="track" class="modal-track" :with-top-scroller="true">
            <div class="inventory-item-form">
              <div class="inventory-item-form__wrapper">
                <div class="modal-frame__header">
                  <h2 class="modal-frame__title">
                    <template v-if="item">
                      Редактирование ТМЦ <span class="inventory-item__name">№{{ item.serialNumber }}</span>
                    </template>
                    <template v-else> Новая ТМЦ </template>
                  </h2>
                  <div class="modal-frame__action">
                    <c-button
                      v-if="item && canRemove"
                      class="inventory-item-modal__remove"
                      size="small"
                      label="Удалить"
                      :icon="{ icon: 'trash', color: 'secondary' }"
                      @click="remove"
                    />
                  </div>
                </div>

                <div v-if="loading" class="inventory-item-form__container">
                  <c-loader
                    class="inventory-items-table__loading"
                    :active="true"
                    label="Данные загружаются"
                    :height="200"
                  ></c-loader>
                </div>

                <div v-else class="inventory-item-form__container">
                  <div v-if="!item" class="row">
                    <div class="col col-12">
                      <div class="form-group inventory-item-form__amount-type">
                        <c-switcher v-model="form.amountType" :items="amountTypes" class="h-switcher" type="group" />
                      </div>
                    </div>
                  </div>

                  <div v-if="item" class="row">
                    <div class="col col-12 col-md-11 col-xl-4">
                      <div class="form-group inventory-item-form__category">
                        <div class="c-input-label">Категория/подкатегория ТМЦ</div>
                        {{ form.category.formatLabel || form.category.label }}
                      </div>
                    </div>

                    <div class="col col-12 col-md-11 col-xl-4">
                      <div class="form-group inventory-item-form__type">
                        <div class="c-input-label">Тип ТМЦ</div>
                        {{ form.type.label }}
                      </div>
                    </div>
                  </div>

                  <div v-else class="row">
                    <div class="col col-12 col-md-11 col-xl-6">
                      <div
                        class="form-group inventory-item-form__category"
                        :class="{
                          'is-invalid': isSubmitted && categoryError,
                        }"
                      >
                        <c-input
                          v-model="form.category"
                          type="select"
                          :items="categories"
                          :searchable="true"
                          :clearable="true"
                          :required-mark="true"
                          :with-categorized="true"
                          floating-label="Категория/подкатегория ТМЦ"
                          :required="isSubmitted && categoryError"
                          :required-condition="isSubmitted && categoryError"
                        />

                        <div class="catalog-link">
                          <a href="?r=redesign#/reference-uniform" target="_blank">Структура каталога ТМЦ</a>
                        </div>
                      </div>
                    </div>

                    <div class="col col-12 col-md-11 col-xl-4">
                      <div
                        class="form-group inventory-item-form__type"
                        :class="{
                          'is-invalid': isSubmitted && typeError,
                        }"
                      >
                        <c-input
                          v-model="form.type"
                          type="select"
                          :items="types"
                          :searchable="true"
                          :clearable="true"
                          :disabled="!form.category"
                          :required-mark="true"
                          floating-label="Тип ТМЦ"
                          :required="isSubmitted && typeError"
                          :required-condition="isSubmitted && typeError"
                        />
                      </div>
                    </div>
                  </div>

                  <div class="row">
                    <div class="col col-12 col-md-8 col-xl-6">
                      <div class="form-group inventory-item-form__name">
                        <c-input
                          v-model="form.name"
                          floating-label="Наименование"
                          :clearable="true"
                          :max-length="255"
                        ></c-input>
                      </div>
                    </div>

                    <div v-if="form.amountType === 'multiple'" class="col col-12 col-md-3 col-xl-2">
                      <div
                        class="form-group inventory-item-form__amount"
                        :class="{
                          'is-invalid': isSubmitted && amountError,
                        }"
                      >
                        <c-input
                          v-model="form.amount"
                          type="price-number"
                          :fractional-length="0"
                          :split="true"
                          floating-label="Кол-во, шт"
                          :required-mark="true"
                          :clearable="true"
                          :required="isSubmitted && amountError"
                          :required-condition="isSubmitted && amountError"
                          :max-length="11"
                        ></c-input>
                      </div>
                    </div>

                    <div v-else class="col col-12 col-md-3 col-xl-2">
                      <div
                        class="form-group inventory-item-form__serial-number"
                        :class="{
                          'is-invalid': isSubmitted && serialNumberError,
                        }"
                      >
                        <c-input
                          v-model="form.serialNumber"
                          floating-label="Серийный номер"
                          :clearable="true"
                          :max-length="255"
                          :required="isSubmitted && serialNumberError"
                          :required-message="isSubmitted && serialNumberError"
                        ></c-input>
                      </div>
                    </div>
                  </div>

                  <div class="row">
                    <div class="col col-6 col-md-3 col-xl-2">
                      <div
                        class="form-group inventory-item-form__expiration-date"
                        :class="{
                          'is-invalid': isSubmitted && expirationDateError,
                        }"
                      >
                        <c-input
                          ref="expirationDate"
                          v-model="form.expirationDate"
                          class="inventory-item-expiration__field"
                          type="date"
                          label="Срок годности"
                          :editable="true"
                          :clearable="true"
                          format="DD.MM.YYYY, dddd"
                          placeholder="00.00.0000"
                          icon="calendar"
                          :icon-right="true"
                          :required="isSubmitted && expirationDateError"
                          :required-message="isSubmitted && expirationDateError"
                        />
                      </div>
                    </div>

                    <div class="col col-6 col-md-3 col-xl-2">
                      <div
                        class="form-group inventory-item-form__production-date"
                        :class="{
                          'is-invalid': isSubmitted && productionDateError,
                        }"
                      >
                        <c-input
                          ref="productionDate"
                          v-model="form.productionDate"
                          label="Дата производства"
                          type="date"
                          :editable="true"
                          :clearable="true"
                          format="DD.MM.YYYY, dddd"
                          icon="calendar"
                          placeholder="00.00.0000"
                          :icon-right="true"
                          :required="isSubmitted && productionDateError"
                          :required-message="isSubmitted && productionDateError"
                        />
                      </div>
                    </div>
                    <div class="col col-6 col-md-3 col-xl-2">
                      <div
                        class="form-group inventory-item-form__purchase-date"
                        :class="{
                          'is-invalid': isSubmitted && purchaseDateError,
                        }"
                      >
                        <c-input
                          ref="purchaseDate"
                          v-model="form.purchaseDate"
                          label="Дата покупки"
                          type="date"
                          :editable="true"
                          :clearable="true"
                          format="DD.MM.YYYY, dddd"
                          icon="calendar"
                          placeholder="00.00.0000"
                          :icon-right="true"
                          :required="isSubmitted && purchaseDateError"
                          :required-message="isSubmitted && purchaseDateError"
                        />
                      </div>
                    </div>
                  </div>

                  <div class="row align-items-end">
                    <div class="col col-12 col-md-8 col-xl-4">
                      <div class="form-group inventory-item-form__model">
                        <c-input
                          v-model="form.model"
                          floating-label="Модель"
                          :clearable="true"
                          :max-length="255"
                        ></c-input>
                      </div>
                    </div>
                    <div class="col col-12 col-md-8 col-xl-4">
                      <div class="form-group inventory-item-form__purchasePlace">
                        <c-input
                          v-model="form.purchasePlace"
                          floating-label="Где приобретено"
                          :clearable="true"
                          :max-length="255"
                        ></c-input>
                      </div>
                    </div>
                    <div class="col col-12 col-md-3 col-xl-2">
                      <div class="form-group inventory-item-form__cost">
                        <c-input
                          v-model="form.cost"
                          :label="
                            form.amountType === 'multiple' ? 'Примерная стоимость за шт., ฿' : 'Примерная стоимость, ฿'
                          "
                          type="price-number"
                          :fractional-length="0"
                          :split="true"
                          :clearable="true"
                          :required="isSubmitted && costError"
                          :required-condition="isSubmitted && costError"
                          :max-length="11"
                          placeholder="0"
                        ></c-input>
                      </div>
                    </div>
                  </div>

                  <div class="row">
                    <div class="col col-12 col-md-11 col-xl-10">
                      <div class="form-group inventory-item-form__description">
                        <c-textarea
                          v-model="form.description"
                          floating-label="Описание"
                          :clearable="true"
                          :max-length="500"
                        ></c-textarea>
                      </div>
                    </div>
                  </div>

                  <div class="form-group inventory-item-form__files">
                    <file-loader
                      ref="loader"
                      v-model="form.files"
                      format="all"
                      actions="loadFiles,preview"
                      :links="form.fileLinks"
                      :limit="10"
                      @changeLinks="form.fileLinks = $event"
                    >
                      <template #note>
                        <div class="file-loader-note">
                          Допустимые форматы: .jpeg, .jpg, .gif, .png, видео: .mp4, .avi, .wmv, .mov, .3gp, .flv,
                          .mpeg-1,2, .webm, документы:.doc, .pdf, .docx. Размер файла — не более 10Мб.
                        </div>
                      </template>
                    </file-loader>
                  </div>

                  <div
                    class="inventory-item-form__actions"
                    :class="{
                      fixed: fixActions,
                    }"
                  >
                    <c-button size="large" class="inventory-item-form__reset" label="Отменить" @click="close" />

                    <c-button
                      class="inventory-item-form__save"
                      :label="saveButtonLabel"
                      color="primary"
                      size="large"
                      @click="submit"
                    />

                    <observer
                      class="footer-observer"
                      @fullyVisible="fixActions = false"
                      @fullyHidden="fixActions = true"
                    />
                  </div>
                </div>
              </div>
            </div>
          </shadow-scroll-container>
        </div>

        <div class="modal-actions">
          <c-button size="large" label="Отменить" @click="close" />

          <c-button :label="saveButtonLabel" color="primary" size="large" @click="submit" />
        </div>
      </div>
    </c-sidesheet>
  </div>
</template>

<script>
import CSidesheet from "@/components/sidesheet";
import CInput from "@/components/controls2/Input";
import CButton from "@/components/controls2/Button";
import CSwitcher from "@/components/controls2/Switcher";
import CTextarea from "@/components/controls/textarea";
import CLoader from "@/components/loader";
import FileLoader from "@/components/file-loader";
import Observer from "@/components/IntersectionObserver";
import ShadowScrollContainer from "@/components/shadow-scroll-container";
import RemoveItemModal from "../RemoveItemModal";
import { ItemModel } from "../../models/ItemModel";

import { AMOUNT_TYPE_MULTIPLE, AMOUNT_TYPE_SINGLE } from "./constants";

import * as ItemForm from "./ItemForm";

export default {
  components: {
    CSidesheet,
    CInput,
    CLoader,
    CButton,
    CSwitcher,
    CTextarea,
    Observer,
    FileLoader,
    ShadowScrollContainer,
    RemoveItemModal,
  },

  props: ["canRemove"],

  data() {
    return {
      isSubmitted: false,
      loading: false,
      fixActions: false,

      item: null,
      id: null,
      active: false,

      singleItem: AMOUNT_TYPE_SINGLE,

      amountTypes: [
        { label: "Одна ТМЦ", value: AMOUNT_TYPE_SINGLE },
        { label: "Несколько ТМЦ", value: AMOUNT_TYPE_MULTIPLE },
      ],

      types: [],

      form: ItemForm.form,


      serverErrors: ItemForm.serverErrors,
    };
  },

  computed: {
    catalog() {
      return this.$store.state.uniformItems.catalog;
    },
    saveButtonLabel() {
      if (this.form.amountType === AMOUNT_TYPE_SINGLE) return "Сохранить";
      const count = this.form.amount || 0;
      return `Сохранить ${count} шт.`;
    },
    costError()  {
      const  v = this.form.cost;
      if (v) {
        if (isNaN(v) || Math.round(v)!=v || v<0) {
          return  "Некорректное значение";
        }
      }
      return '';
    },
    categories() {
      const list = [];

      this.catalog.forEach((category) => {
        const {
          id: categoryId,
          name: categoryName,
          categories: subcategories = [],
          types: categoryTypes = [],
        } = category;

        let fullCategoryTypesList = [];

        const notEmptyCategories = subcategories.filter((subcategory) => subcategory.types.length);
        const subcategoryList = notEmptyCategories.map((subcategory) => {
          const { id, name, types } = subcategory;

          fullCategoryTypesList = [...fullCategoryTypesList, ...types];

          return {
            value: id,
            label: name,
            resultLabel: `${categoryName}/${name}`,
            isSubCategory: true,
            level: 1,
            types,
          };
        });

        fullCategoryTypesList = [...fullCategoryTypesList, ...categoryTypes];

        const categoryModel = {
          value: categoryId,
          label: categoryName,
          isSubCategory: false,
          level: 0,
          types: fullCategoryTypesList,
          subcategories: subcategoryList,
        };

        if (categoryTypes.length || subcategoryList.length) {
          list.push(categoryModel);
          subcategoryList.forEach((i) => list.push(i));
        }
      });

      return list;
    },

    ...ItemForm.computedErrors,
    isValid() {
      const errors = Object.keys(ItemForm.computedErrors).map((key) => this[key]);
      return !errors.some(Boolean) && !this.costError;
    },
  },

  watch: {

    "form.category": function (v) {
      const { type: prevType } = this.form;
      this.form.type = null;

      if (v) {
        const types = v.types.map((type) => ({ value: type.id, label: type.name }));
        types.sort((a, b) => (a.label.toLowerCase() < b.label.toLowerCase() ? -1 : 1));
        this.types = types;
      } else {
        this.types = [];
      }

      if (prevType) {
        const type = this.types.find((t) => t.value === prevType.value);
        if (type) this.form.type = type;
      }
    },

    ...ItemForm.watchers,
  },

  created() {
    document.getElementsByTagName("body")[0].addEventListener("click", () => {
      this.$root.$emit("bv::hide::popover");
    });

    document.addEventListener("keyup", (evt) => {
      if (evt.keyCode === 27) {
        if (this.active) {
          this.close();
        }
      }
    });
  },

  methods: {
    open(item) {
      this.reset();

      if (item) {
        this.loading = true;
        this.$store.dispatch("uniformItems/getItem", item.id).then((data) => {
          this.item = ItemModel(data);
          ItemForm.setData(this.item, this.categories);
          this.updateDates();
          this.loading = false;
        });
      }

      this.active = true;
    },

    close() {
      this.active = false;
      this.reset();
      this.$emit("close");
    },
    remove() {
      this.$refs.removeItemModal.open(this.item);
    },
    onRemove() {
      this.close();
      this.$nextTick(() => this.$emit("remove", this.item));
    },
    getParams() {
      const params = ItemForm.getData();

      if (this.item) params.id = this.item.id;

      return params;
    },
    submit() {
      this.isSubmitted = true;
      if (!this.isValid) return;

      const editing = !!this.item;
      const action = editing ? "uniformItems/updateItem" : "uniformItems/createItem";

      this.$store
        .dispatch(action, this.getParams())
        .then((res) => {
          if (res.success) {
            this.isSubmitted = false;

            let message = "";

            if (this.form.amountType === AMOUNT_TYPE_MULTIPLE) {
              message = res.message;
            } else {
              const { guid, name } = res.model;

              const nameText = name ? `(${name}) ` : "";
              const statusText = editing ? "изменена" : "добавлена";

              message = `Позиция <b>№${guid} ${nameText}</b> успешно ${statusText}`;
            }

            this.$notify({
              group: "success-message",
              data: {
                textMessage: message,
              },
            });
            this.close();
            this.$nextTick(() => this.$emit(editing ? "updated" : "created", res.model));
          } else {
            console.error(res);

            ItemForm.setServerErrors(res.errors);
            this.$notify({
              group: "error-message",
              data: {
                textMessage: res.errors[Object.keys(res.errors)[0]],
              },
            });
          }
        })
        .catch((err) => {
          const response = err && err.response;
          if (response) {
            if (response.data) {
              const { errors } = response.data;
              if (errors && Object.keys(errors).length) {
                ItemForm.setServerErrors(errors);
                console.log(errors);
                this.$notify({
                  group: "error-message",
                  data: {
                    textMessage: errors[Object.keys(errors)[0]],
                  },
                });
                return;
              }
            }
            this.$notify({
              group: "error-message",
              data: {
                textMessage: "Произошла ошибка. Попробуйте ещё раз.",
              },
            });
          }
        });
    },
    updateDates() {
      this.$nextTick(() => {
        ["expirationDate", "productionDate", "purchaseDate"].forEach((key) => {
          const ref = this.$refs[key];
          const value = this.form[key];
          if (ref) ref.setDateInputTypeDate(value);
        });
      });
    },
    reset() {
      this.isSubmitted = false;
      this.item = null;

      this.id = null;

      ItemForm.reset();
      this.updateDates();
      this.$refs.loader.clear();
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/vars";
@import "~@/assets/styles/grid.scss";

/deep/ {
  .sidesheet {
    &__header {
      display: none;
    }
    &__wrapper {
      padding: 0 !important;
      z-index: 1001;
    }

    &__content {
      height: 100%;
      padding: 0;
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }

    &__close {
      padding-right: 10px;
      padding-top: 10px;
    }
  }
}

.modal-frame {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .modal-wrapper {
    flex-grow: 1;
    display: flex;
    overflow: hidden;
  }

  .modal-track {
    width: 100%;
    height: 100%;

    /deep/ {
      .scroll-container {
        padding-right: 0;
      }
    }
  }

  .modal-actions {
    flex-shrink: 0;
    background: white;
    display: flex;

    & button {
      flex-basis: 100%;
      width: 100%;
      border-radius: 0;
      flex-shrink: 1;
    }
  }

  &__header {
    margin-bottom: 30px;
  }

  &__title {
    margin-bottom: 12px;
    font-size: 18px;
    font-weight: 500;
  }

  &__actions {
    margin-bottom: 12px;
    display: flex;
    align-items: center;
  }

  @include bootstrapMd {
    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
    &__title {
      font-size: 22px;
      margin-bottom: 0;
    }
    &__actions {
      margin-bottom: 0;
    }
  }
}

.inventory-item-form {
  &__wrapper {
    padding: 0 20px 20px;
  }

  &__actions {
    position: sticky;
    bottom: 0;
    z-index: 2;
    background: white;
    padding-bottom: 10px;

    &.fixed {
      &:before {
        content: "";
        position: absolute;
        background: white;
        top: -10px;
        bottom: 0;
        left: -30px;
        right: -30px;
        box-shadow: 0 -4px 4px rgba(0, 0, 0, 0.25);
      }
    }

    .footer-observer {
      position: absolute;
      bottom: -1px;
    }
  }

  &__category,
  &__type,
  &__cost {
    padding-top: 6px;
  }

  .is-invalid {
    padding-bottom: 20px;
  }

  &__category.is-invalid {
    padding-bottom: 0;
    .catalog-link {
      margin-top: 22px;
    }
  }

  .catalog-link {
    margin-top: 8px;

    a {
      font-size: 13px;
      font-weight: 500;
    }
  }

  .file-loader-note {
    font-size: 11px;
    font-weight: 400;
    color: #8e9cbb;
    margin-left: 32px;
    max-width: 587px;
    display: flex;
    align-items: center;
    line-height: 1.3;
  }

  &__actions {
    display: none;
    flex-shrink: 0;
  }

  @include onlyMobile {
    .file-loader-note {
      margin-top: 15px;
      margin-left: 0;
    }
  }
}

.inventory-item {
  &__name {
    font-weight: 400;
  }
}

@include bootstrapMd {
  .inventory-item-form {
    @include gridGenerator(11);

    padding-left: 30px;
    padding-right: 30px;
    padding-bottom: 40px;
    padding-top: 25px;

    &__wrapper {
      padding-bottom: 40px;
      padding-left: 0;
      padding-right: 0;
    }

    &__actions {
      display: flex;
      & button {
        margin-right: 10px;
      }
    }
  }
  .modal-frame {
    .modal-actions {
      display: none;
    }
  }
}
</style>
